import {
  Button,
  Card,
  Grid,
  InputLabel,
  Stack,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from "@mui/material";
import React from "react";
import whiteLogo from "../../assets/images/whiteLogo-bgwhite.png";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import TextFieldWrapper from "../../components/FormComponents/TextFieldWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import AuthQuery from "../../stateQueries/Auth";
import EmailIcon from "@mui/icons-material/Email";
import AlertPopup from "../../components/AlertPopup";

const ForgotPassword = ({ currentTheme }) => {
  const navigate = useNavigate();

  const forgotPasswordMutation = useMutation({
    mutationFn: async (formData) => {
      return await AuthQuery.forgotPassword(formData);
    },
    onSuccess: (data) => {
      console.log("Password reset email sent", data);
    },
    onError: (err) => {
      console.log(err);
    }
  });

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #3a9bfb 0%, #163683 50%, #0d2456 100%)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Left Side - Branding */}
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          zIndex: 1
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            animation: "floatAnimation 3s ease-in-out infinite"
          }}
        >
          <img
            src={whiteLogo}
            alt="Logo"
            style={{
              height: "120px",
              marginBottom: "2rem",
              filter: "drop-shadow(0 4px 15px rgba(0,0,0,0.15))"
            }}
          />
          <Typography
            variant="h3"
            sx={{
              color: "white",
              fontWeight: 700,
              mb: 2,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)"
            }}
          >
            Password Recovery
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.95)",
              maxWidth: "400px",
              lineHeight: 1.6
            }}
          >
            Enter your email and we'll send you instructions to reset your
            password
          </Typography>
        </Box>
      </Stack>

      {/* Right Side - Form */}
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        sx={{ position: "relative", zIndex: 1, px: 2 }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: "480px",
            p: 5,
            position: "relative",
            overflow: "visible"
          }}
        >
          {forgotPasswordMutation.isSuccess && (
            <AlertPopup
              open={true}
              message={
                forgotPasswordMutation.data?.message ||
                "Password reset link sent to your email"
              }
            />
          )}

          {forgotPasswordMutation.isError && (
            <AlertPopup
              open={true}
              message={
                forgotPasswordMutation.error?.response?.data?.message ||
                "Failed to send reset link"
              }
              severity="error"
            />
          )}

          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              position: "absolute",
              top: 20,
              left: 20,
              color: "primary.main"
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ textAlign: "center", mb: 4, mt: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: currentTheme === "light" ? "text.primary" : "white",
                mb: 1
              }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color:
                  currentTheme === "light"
                    ? "text.secondary"
                    : "rgba(255,255,255,0.7)"
              }}
            >
              We'll email you a link to reset your password
            </Typography>
          </Box>

          <Formik
            initialValues={{
              email: ""
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email("Please provide a valid email")
                .required("Email is required")
            })}
            onSubmit={(values) => {
              forgotPasswordMutation.mutate(values);
            }}
          >
            {() => {
              return (
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <InputLabel
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color:
                            currentTheme === "light" ? "text.primary" : "white"
                        }}
                      >
                        Email Address
                      </InputLabel>
                      <TextFieldWrapper
                        name="email"
                        placeholder="Enter your email"
                        type="email"
                        fullWidth
                        startIcon={<EmailIcon sx={{ color: "primary.main" }} />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              currentTheme === "light"
                                ? "rgba(0,0,0,0.02)"
                                : "rgba(255,255,255,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor:
                                currentTheme === "light"
                                  ? "rgba(0,0,0,0.04)"
                                  : "rgba(255,255,255,0.08)"
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        type="submit"
                        disabled={forgotPasswordMutation.isLoading}
                        sx={{
                          py: 1.5,
                          borderRadius: "12px",
                          fontSize: "16px",
                          fontWeight: 600,
                          textTransform: "none",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          boxShadow: "0 8px 24px rgba(102,126,234,0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 32px rgba(102,126,234,0.5)"
                          },
                          "&:disabled": {
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            opacity: 0.7
                          }
                        }}
                      >
                        {forgotPasswordMutation.isLoading ? (
                          <CircularProgress size={24} sx={{ color: "white" }} />
                        ) : (
                          "Send Reset Link"
                        )}
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center" }}>
                        <Button
                          onClick={() => navigate("/login")}
                          sx={{
                            textTransform: "none",
                            color: "primary.main",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "transparent",
                              textDecoration: "underline"
                            }
                          }}
                        >
                          Back to Login
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Card>
      </Stack>

      <style>
        {`
          @keyframes floatAnimation {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}
      </style>
    </Stack>
  );
};

export default ForgotPassword;
